import React, { useRef } from 'react';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { classNames } from 'primereact/utils';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../shared/CustomButton';
import { MdLogout } from "react-icons/md";

import { logout } from '../../redux/slices/AuthSlice';
import { useTranslation } from 'react-i18next';
import { fetchData } from '../../helpers/fetchData';
import toast from 'react-hot-toast';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextInput from '../../shared/TextInput';
import PasswordField from '../../shared/PasswordField';
import ReusableModal from '../../shared/ReuseableModal';
import { FaCheckDouble, FaKey, FaTimes } from 'react-icons/fa';
import CheckPermission from '../../hooks/CheckPermission';

const ConfirmationModal = React.lazy(() => import('../../shared/ConfirmationModal'));

const ProfileDropdown = () => {
  const { hasPermission } = CheckPermission();
  const profileMenu = useRef();
  const dispatch = useDispatch();
  const [openLogoutModal, setOpenLogoutModal] = React.useState(false);
  const [logoutLoading, setLogoutLoading] = React.useState(false);
  const [openChangePasswordModal, setOpenChangePasswordModal] = React.useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = React.useState(false);
  const { user } = useSelector(state => state?.auth);
  const { t, i18n } = useTranslation();

  let profileItems = [
    {
      command: () => { },
      template: (item, options) => {
        return (
          <button
            onClick={e => options.onClick(e)}
            className={classNames(
              options.className,
              'w-full p-link flex items-center text-[16px] py-2 px-3 text-primary rounded-t-md'
            )}
          >
            <Avatar
              label={user?.full_name?.charAt(0)}
              className='h-[40px] text-[18px] text-black w-[40px] font-[500] mr-2'
              shape='circle'
            />
            <div className='flex flex-col align'>
              <span className='capitalize font-semibold overflow-hidden whitespace-nowrap'>
                {user?.full_name}
              </span>
              <span className='text-14-gray'>
                {i18n?.language == 'ar' ? user?.role?.name_ar : user?.role?.name_en || ''}
              </span>
            </div>
          </button>
        );
      }
    },
    {
      className: '!bg-gray-100',
      label: t('Change Password'),
      icon: 'pi pi-key',
      command: () => setOpenChangePasswordModal(true)
    },
    {
      className: '!bg-gray-100',
      label: t('Logout'),
      icon: 'pi pi-sign-out',
      command: () => setOpenLogoutModal(true)
    }
  ];

  const handleLogout = async () => {
    setLogoutLoading(true);
    const response = await fetchData('POST', '/v3/Logout', {});
    setLogoutLoading(false);
    if (response) {
      dispatch(logout());
      sessionStorage.clear()
    }
  };

  const handleChangePassword = async (values, { resetForm }) => {
    if (!hasPermission('staff', 'update') && !hasPermission('management', 'update')) return;
    setChangePasswordLoading(true);
    const payload = {
      current_password: values.oldPassword,
      new_password: values.newPassword,
      confirm_password: values.confirmPassword
    };
    const response = await fetchData('POST', '/v3/change_password', payload);
    setChangePasswordLoading(false);
    if (response) {
      toast.success(response?.message || t('Password changed successfully'));
      setOpenChangePasswordModal(false);
      resetForm();
    }
  };

  const changePasswordSchema = Yup.object().shape({
    oldPassword: Yup.string().required(t('Old Password is required')),
    newPassword: Yup.string()
      .min(6, t('Password must be at least 6 characters'))
      .required(t('New Password is required')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], t('Passwords must match'))
      .required(t('Confirm Password is required'))
  });

  return (
    <div>
      <div onClick={e => profileMenu?.current?.toggle(e)} className='flex cursor-pointer gap-3 items-center'>
        <CustomButton
          buttonType='reset'
          icon='pi pi-angle-down'
          label={user?.full_name}
          onClick={e => profileMenu?.current?.toggle(e)}
          className='!border-0 whitespace-nowrap'
        />
      </div>
      <Menu
        model={profileItems}
        popup
        ref={profileMenu}
        id='popup_profile_menu'
        className='border-none shadow-lg text-14-gray !bg-gray-100 custom-menu mt-4 px-0 py-0'
        pt={{
          root: { className: '!rounded-md' },
          menuitem: { className: 'border-b border-gray-200 py-2' },
          label: { className: '!text-14-dark group-hover:text-primary' },
          action: { className: 'py-3 px-3 bg-gray-100' },
          content: { className: 'bg-transparent group !hover:rounded-md' },
          icon: { className: 'text-14-dark group-hover:text-primary' }
        }}
        style={{ width: '20rem' }}
      />
      <ConfirmationModal
        message='Are you sure you want to log out?'
        Icon={MdLogout}
        visible={openLogoutModal}
        setVisible={setOpenLogoutModal}
        acceptLabel={'Logout'}
        rejectLabel={'Cancel'}
        onAccept={handleLogout}
        isLoading={logoutLoading}
      />
      <ReusableModal
        isVisible={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
        title={t('Change Password')}
        position='top'
        HeaderIcon={FaKey}
        className='w-full md:w-[500px]'
      >
        <Formik
          initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
          validationSchema={changePasswordSchema}
          onSubmit={handleChangePassword}
        >
          {({ isSubmitting }) => (
            <Form className='flex flex-col gap-4 mt-4'>
              <PasswordField
                name='oldPassword'
                label={t('Old Password')}
                placeholder={t('Enter Old Password')}
                toggleMask
              />
              <PasswordField
                name='newPassword'
                label={t('New Password')}
                placeholder={t('Enter New Password')}
                toggleMask
              />
              <PasswordField
                name='confirmPassword'
                label={t('Confirm New Password')}
                placeholder={t('Confirm New Password')}
                toggleMask
              />
              <div className='flex justify-end gap-2 mt-2'>
                <CustomButton
                  label={t('Cancel')}
                  buttonType='reset'
                  icon={FaTimes}
                  onClick={() => setOpenChangePasswordModal(false)}
                />
                {(hasPermission('staff', 'update') || hasPermission('management', 'update')) && (
                  <CustomButton
                    type='submit'
                    icon={FaCheckDouble}
                    label={t('Update Password')}
                    loading={changePasswordLoading}
                  />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </ReusableModal>
    </div>
  );
};

export default ProfileDropdown;
